import { useCallback, useEffect, useState } from 'react';
import { useDocumentPreview } from '../../../api/agents-client/agent-client.hooks';
import { isSafeUrl } from '../../../helpers/general-helpers';

/**
 * How long (ms) to wait for a ready PDF's iframe to fire `onLoad` before
 * treating it as failed. Covers the silent-blank case where a CSP `frame-src`
 * block or network error prevents the frame from loading with no `onError`.
 */
const FRAME_LOAD_TIMEOUT_MS = 12_000;

/**
 * PDF open parameters appended to a preview iframe's `src` to hide the browser's
 * native PDF toolbar (download, print, and the "save to Drive"/overflow menu)
 * and side thumbnail pane, for a clean embedded preview. Append to the iframe
 * `src` only — never to a pop-out/open-in-new-tab link, which is the escape
 * hatch to the full native controls.
 *
 * This is presentation, not a download lock: the file stays reachable via the
 * card's download control, the pop-out, right-click, and Ctrl+S. Honored by
 * Chromium's viewer (Chrome/Edge); Firefox (pdf.js) and Safari ignore it and
 * keep their own toolbar. A URL fragment is client-side only, so it never
 * reaches S3 and cannot break the presigned request.
 */
export const PDF_EMBED_PARAMS = '#toolbar=0&navpanes=0';

interface DocumentPreviewFrame {
  /** The presigned PDF url to render, once ready and safe; otherwise undefined. */
  safePreviewUrl: string | undefined;
  /** True when conversion, request, timeout, or frame-load failed. */
  failed: boolean;
  /** Whether the loading overlay should show (converting or frame not yet loaded). */
  overlayVisible: boolean;
  /** Pass to the iframe's `onLoad` so the overlay clears once it renders. */
  onFrameLoad: () => void;
  /** Clears every failure latch and polls again from scratch. */
  retry: () => void;
}

/**
 * Shared state machine for previewing a Word/Excel document as a
 * server-converted PDF in an iframe. Wraps {@link useDocumentPreview}
 * (poll-until-ready + timeout) and adds a frame-load watchdog so a
 * ready-but-unrenderable url (CSP/network block, which fires no `onError`)
 * resolves to `failed` instead of a silent blank frame. Both the chat rail panel
 * and the Documents-page modal render their own chrome around this.
 *
 * @param {string | undefined} documentId - The document to preview; disabled when absent
 * @param {number | undefined} revision - Specific revision to preview; defaults to current
 * @returns {DocumentPreviewFrame} The derived url, failure/overlay flags, and load handler
 */
export function useDocumentPreviewFrame(documentId: string | undefined, revision?: number): DocumentPreviewFrame {
  const { preview, error, timedOut, retry: retryPreview } = useDocumentPreview(documentId, revision);

  const previewUrl = preview?.status === 'ready' && preview.preview_url ? preview.preview_url : undefined;
  const safePreviewUrl = previewUrl && isSafeUrl(previewUrl) ? previewUrl : undefined;

  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  // The url a frame-load timeout belongs to, rather than a bare boolean, so a
  // stale timeout from a previous document/revision cannot mark a freshly
  // loading preview as failed once `safePreviewUrl` changes.
  const [timedOutUrl, setTimedOutUrl] = useState<string | null>(null);
  const frameReady = !!safePreviewUrl && loadedUrl === safePreviewUrl;

  useEffect(() => {
    if (!safePreviewUrl || frameReady) return;
    const timer = setTimeout(() => setTimedOutUrl(safePreviewUrl), FRAME_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [safePreviewUrl, frameReady]);

  // Count the frame-load timeout only when it belongs to the url currently being
  // shown and that frame has not since loaded. When the document or revision
  // changes, `safePreviewUrl` becomes undefined (processing) or a new url, so a
  // timeout captured for the previous url no longer applies.
  const frameTimedOut = !!safePreviewUrl && !frameReady && timedOutUrl === safePreviewUrl;
  // A "ready" response whose url is missing or unsafe yields no `safePreviewUrl`,
  // so the frame watchdog never starts and the overlay would spin forever. Treat
  // that as a failure so the caller shows its error state instead.
  const invalidReadyPreview = preview?.status === 'ready' && !safePreviewUrl;
  const failed = !!error || preview?.status === 'error' || invalidReadyPreview || timedOut || frameTimedOut;
  const overlayVisible = !failed && !frameReady && !!documentId;

  // Both latches are url-scoped, so a retry has to clear them explicitly: the
  // backend can hand back the very same presigned url, in which case comparing
  // against `safePreviewUrl` would leave the frame permanently marked failed.
  const retry = useCallback(() => {
    setTimedOutUrl(null);
    setLoadedUrl(null);
    retryPreview();
  }, [retryPreview]);

  return {
    safePreviewUrl: failed ? undefined : safePreviewUrl,
    failed,
    overlayVisible,
    onFrameLoad: () => setLoadedUrl(safePreviewUrl ?? null),
    retry,
  };
}
